import { cluesEqual, lineClues } from "./clues";
import type { CellMark } from "./types";

/**
 * The auto-cross assist: when a line's filled squares already spell out its whole
 * clue, every remaining unknown square in that line is provably empty, so the
 * player would only be transcribing crosses the clues have already earned.
 *
 * This is the deduction a player makes on paper — it reads the player's own marks,
 * never the solution, so a wrong fill still leads to wrong crosses and Undo is the
 * honest way out, exactly as it would be with a pencil.
 *
 * Returns the crossed board when anything changed, or undefined so a caller can
 * skip a state update (and a history entry) for lines that were already done.
 */
export function autoCrossCells(
  cells: readonly CellMark[],
  size: number,
  rowClues: readonly (readonly number[])[],
  columnClues: readonly (readonly number[])[],
): CellMark[] | undefined {
  let changed = false;
  const next = [...cells];

  const crossIfSettled = (lineIndices: number[], clues: readonly number[]): void => {
    // Without an unknown square there is nothing to write, and without a filled one
    // the line cannot have earned its clue yet — [] matches [] on an empty line, but
    // an untouched all-unknown line must stay untouched.
    let hasUnknown = false;
    let hasFill = false;
    for (const index of lineIndices) {
      const mark = next[index] ?? 0;
      if (mark === 0) hasUnknown = true;
      else if (mark === 1) hasFill = true;
    }
    if (!hasUnknown || !hasFill) return;

    const filled = lineIndices.map((index) => (next[index] === 1 ? 1 : 0));
    if (!cluesEqual(lineClues(filled), clues)) return;

    for (const index of lineIndices) {
      if (next[index] === 0) {
        next[index] = -1;
        changed = true;
      }
    }
  };

  for (let row = 0; row < size; row += 1) {
    crossIfSettled(
      Array.from({ length: size }, (_, column) => row * size + column),
      rowClues[row] ?? [],
    );
  }
  for (let column = 0; column < size; column += 1) {
    crossIfSettled(
      Array.from({ length: size }, (_, row) => row * size + column),
      columnClues[column] ?? [],
    );
  }

  return changed ? next : undefined;
}
